import { readonly, ref } from 'vue';
import { http } from '@/interceptors/axiosRequestInterceptor';

import type { ImpersonationInfo } from '@/composables/impersonation';
import type { Role, User } from '@/models';
import { currentIsDark, onThemeChange } from '@/composables/useTheme';
import { envFeatureEnabled } from '@/utils/featureFlags';

/**
 * The Zendesk support widget in the admin portal (AB#21165).
 *
 * One widget, one brand: every ticket raised here lands in DDI regardless of who
 * raised it. The portal is used by Custimoo staff, merchant admins and factory
 * users alike, and "who is this person" is the first thing whoever triages needs
 * to know — so who is asking rides along on every conversation as tags and
 * ticket fields.
 *
 * This is the MESSAGING widget. The older classic widget would have let us set an
 * identity outright (`identify`/`prefill`, no token), but classic is legacy at
 * Zendesk and only available on accounts created before 5 June 2023 — ours is not
 * one. Messaging trusts identity only from a JWT signed with a key that lives in
 * the backend, so two things follow and both are deliberate:
 *
 *   - APPEARANCE IS NOT SET HERE. Colour and the conversation window are
 *     configured per-brand in Zendesk Admin Center. `zESettings` is a classic-only
 *     mechanism and is ignored by messaging; setting it here would look like it
 *     worked and change nothing. The one exception is the LAUNCHER: Zendesk's
 *     bubble is hidden and the portal draws its own (AB#21684, see
 *     `useAtlasLauncher` below) because Messaging cannot put an image on it.
 *   - IDENTITY IS SIGNED, WHEN IT CAN BE. `zendesk/messaging-token` (AB#21166)
 *     signs the signed-in user so Zendesk shows a requester rather than
 *     "Visitor". When that endpoint is missing, unconfigured or unreachable the
 *     visitor stays anonymous and everything else carries on — so this file
 *     works the same before and after the backend ships.
 *
 *     Either way the conversation carries what we know about the sender as tags
 *     and ticket fields. Those are set by the browser, so they are what the
 *     sender SAYS about themselves; the JWT is the part that is proof.
 *
 * NOTHING HERE IS A SECRET. The widget key is public by design: it identifies
 * which widget to load, not who may use it. The ticket-field ids are just ids.
 */

type ZendeskCommand = (...args: unknown[]) => void;

declare global {
  interface Window {
    zE?: ZendeskCommand;
  }
}

const SCRIPT_ID = 'ze-snippet';

export const widgetKey = (): string => String(import.meta.env.VITE_ZENDESK_WIDGET_KEY || '');

export const isConfigured = (): boolean => widgetKey() !== '' && envFeatureEnabled('zendesk');

const slug = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]+/g, '_');

/** Who the ticket is FROM — not always the session it is raised in. */
type Actor = ImpersonationInfo['by'];

/**
 * Tags that travel with the conversation and end up on the ticket.
 *
 * Deliberately about WHO is asking rather than what they asked: DDI receives
 * tickets from staff, merchants and factories in one queue, and the difference
 * decides where it goes next.
 *
 * During impersonation the two halves come from different people on purpose, and
 * `impersonating` is there so nobody has to guess which: `role_` is the REAL
 * admin's, because they are the one raising the ticket, while `company_` stays
 * the company they are looking at, because that is what the ticket is about.
 */
export const ticketTags = (user?: User, role?: Role, impersonatedBy?: Actor | null): string[] => {
  const tags = ['admin_v2'];

  const roleName = impersonatedBy?.role || role?.name || user?.roles?.[0]?.name;
  if (roleName) tags.push('role_' + slug(roleName));

  if (user?.company_id) tags.push('company_' + String(user.company_id));

  if (impersonatedBy) tags.push('impersonating');

  return tags;
};

export const displayName = (user?: User): string => {
  if (!user) return '';
  const full = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return full || user.name || '';
};

/**
 * Who the ticket is from.
 *
 * The widget is not hidden during impersonation, because impersonation is how
 * the team works: an admin finds the problem while viewing as the merchant, and
 * that is precisely the moment they want to report it.
 *
 * So the conversation carries the REAL admin — the person at the keyboard, who
 * can answer a follow-up — rather than the account they happen to be viewing
 * through. The impersonated user never sees this widget and never asked for
 * anything; a ticket in their name with their email on it would be answered to
 * someone who knows nothing about it.
 */
export const requester = (
  user?: User,
  impersonatedBy?: Actor | null
): { name: string; email: string } =>
  impersonatedBy
    ? { name: impersonatedBy.name || '', email: impersonatedBy.email || '' }
    : { name: displayName(user), email: user?.email || '' };

export interface ConversationField {
  id: string;
  value: string;
}

/**
 * The custom ticket fields in the DDI Zendesk account.
 *
 * Defaults in code rather than required configuration, because a Zendesk ticket
 * field id is global to the account: it is the same value in dev, staging and
 * production, so there is nothing per-environment to decide. What IS
 * per-environment is whether the widget runs at all, and the widget key already
 * decides that.
 *
 * The alternative — three more variables that each have to be set correctly —
 * fails quietly in the one direction that matters: miss one and tickets keep
 * arriving, just without a name on them, and nobody finds out until an agent
 * asks who they are talking to. VITE_ZENDESK_FIELD_* still overrides all three,
 * which is what a different Zendesk account would need.
 */
const FIELD_IDS = {
  name: '50820864653713',
  email: '50820857455249',
  userId: '50820866509585',
};

const fieldId = (override: unknown, fallback: string): string => String(override || '') || fallback;

/**
 * Custom ticket fields carrying the sender's name, email and user id.
 *
 * These exist because messaging tickets are raised anonymously until the JWT
 * lands (AB#21166): without them an agent sees a conversation from nobody in
 * particular. A field is skipped when we have no value for it — an empty string
 * would overwrite nothing with nothing and read as though we looked and found
 * the user had no email.
 */
export const conversationFields = (
  user?: User,
  impersonatedBy?: Actor | null
): ConversationField[] => {
  const { name, email } = requester(user, impersonatedBy);
  const env = import.meta.env;

  const fields: ConversationField[] = [];
  const add = (id: string, value: string) => {
    if (id && value) fields.push({ id, value });
  };

  add(fieldId(env.VITE_ZENDESK_FIELD_NAME, FIELD_IDS.name), name);
  add(fieldId(env.VITE_ZENDESK_FIELD_EMAIL, FIELD_IDS.email), email);
  // The portal user, not the impersonator: this is the account the problem was
  // seen through, and the one an agent would need to look up.
  add(fieldId(env.VITE_ZENDESK_FIELD_USER_ID, FIELD_IDS.userId), user?.id ? String(user.id) : '');

  return fields;
};

/**
 * Load the widget script, and tell us when it is actually usable.
 *
 * The `onReady` callback is the whole point: `zE` does not exist until the
 * snippet has run, so anything we send before then goes nowhere. Silently — the
 * wrapper below swallows it — which is exactly the kind of bug that ships.
 */
const loadScript = (key: string, onReady: () => void): void => {
  const existing = document.getElementById(SCRIPT_ID);
  if (existing) {
    if (window.zE) onReady();
    else existing.addEventListener('load', onReady, { once: true });
    return;
  }

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = 'https://static.zdassets.com/ekr/snippet.js?key=' + encodeURIComponent(key);
  script.addEventListener('load', onReady, { once: true });
  document.head.appendChild(script);
};

const zE = (...args: unknown[]): void => {
  try {
    window.zE?.(...args);
  } catch {
    // The widget is a third-party script on someone else's infrastructure. It
    // failing must never take a page of the admin with it.
  }
};

/**
 * Attach everything we know about the sender to the conversation.
 *
 * Called at startup AND again every time the widget is opened or a new
 * conversation is started (see `startZendeskWidget`), because Zendesk copies
 * this metadata onto the ticket at the moment the ticket is CREATED and it does
 * not survive the conversation that used it:
 *
 *   - metadata set once at page load is stale, or gone, by the time someone
 *     opens the widget an hour into a session;
 *   - Zendesk resets conversation metadata server-side once a ticket closes, so
 *     a second conversation in the same page load would carry none of it.
 *
 * Re-sending is idempotent and costs nothing — it writes to the widget's local
 * state, not the network.
 */
export const describeSender = (user?: User, role?: Role, impersonatedBy?: Actor | null): void => {
  if (!user) return;

  zE('messenger:set', 'conversationTags', ticketTags(user, role, impersonatedBy));

  const fields = conversationFields(user, impersonatedBy);
  if (fields.length) zE('messenger:set', 'conversationFields', fields);
};

/**
 * Ask the backend to sign who we are, so Zendesk shows a requester instead of
 * "Visitor" (AB#21166).
 *
 * Returns null whenever we cannot get a token — no endpoint yet, not configured,
 * a network blip — and null means "stay anonymous", which is exactly how the
 * widget already behaves. That is the point: this ships before the backend does
 * and is inert until the endpoint answers, so neither side has to wait for the
 * other or land in the same window.
 */
export const fetchIdentityToken = async (): Promise<string | null> => {
  try {
    const { data } = await http.get('zendesk/messaging-token');
    const token = data?.result?.token;

    return typeof token === 'string' && token !== '' ? token : null;
  } catch {
    // A 404 before the endpoint exists, a 503 when it is unconfigured, or an
    // outage. None of them is worth a toast over the order screen: the visitor
    // is anonymous, the ticket still carries the tags and fields, and support
    // still works.
    return null;
  }
};

/**
 * Hand Zendesk a verified identity.
 *
 * NOT called during impersonation, and that is a correctness decision rather
 * than caution. The endpoint signs whoever the API sees, which in an
 * impersonated session is the account being viewed THROUGH — the backend cannot
 * tell, because the impersonation stash lives in the legacy frontend's
 * localStorage and never reaches the API. Logging in there would put a verified
 * stamp on the wrong person, which is worse than no stamp: the conversation
 * fields already name the real admin, honestly marked as self-reported.
 *
 * `loginUser` takes a callback rather than a token because Zendesk re-asks when
 * its token expires or is rejected. Ours re-fetches, so an hour-long token costs
 * nothing and a rejected one heals itself.
 */
const login = (): void => {
  zE('messenger', 'loginUser', (callback: (token: string) => void) => {
    fetchIdentityToken().then((token) => {
      if (token) callback(token);
      // No callback call on failure: Zendesk keeps the session anonymous, which
      // is where it already was.
    });
  });
};

/**
 * Make the widget follow the portal's light / dark theme (AB#21595).
 *
 * The file header is right that classic `zESettings` is ignored by messaging —
 * but the MESSAGING widget does take a live palette through
 * `messenger:set customization`, which is a different API, so this is the one
 * lever that works. Admin Center still owns the default a first-time visitor
 * sees; this overrides it per session to match whichever mode the portal is in,
 * and re-sends on every toggle so the two never disagree.
 *
 * If a Zendesk account ignores the customization (older widget versions), the
 * call is a no-op — `zE` swallows failures — so this is safe to ship regardless.
 */
const lightTheme = {
  primary: '#4F46E5',
  onPrimary: '#FFFFFF',
  action: '#4F46E5',
  onAction: '#FFFFFF',
  background: '#FFFFFF',
  onBackground: '#000000',
  message: '#F1F1F6',
  onMessage: '#1A1A1A',
  businessMessage: '#F4F6F8',
  onBusinessMessage: '#000000',
};

const darkTheme = {
  primary: '#818CF8',
  onPrimary: '#000000',
  action: '#818CF8',
  onAction: '#000000',
  background: '#1F1F1F',
  onBackground: '#FFFFFF',
  conversationListBackground: '#1F1F1F',
  onConversationListBackground: '#FFFFFF',
  message: '#2A2A2A',
  onMessage: '#FFFFFF',
  businessMessage: '#2A2A2A',
  onBusinessMessage: '#FFFFFF',
};

export const applyZendeskTheme = (isDark: boolean): void => {
  zE('messenger:set', 'customization', { theme: isDark ? darkTheme : lightTheme });
};

export const show = (): void => zE('messenger', 'show');
export const hide = (): void => zE('messenger', 'hide');
export const open = (): void => zE('messenger', 'open');

/**
 * AB#21684 — the Ask Atlas launcher is ours, not Zendesk's.
 *
 * Messaging lets a brand upload an avatar for the conversation header but not
 * for the round launcher bubble, which only takes a colour and a shape. So the
 * portal draws its own launcher (AtlasLauncher.vue, the Atlas portrait) and
 * Zendesk's is hidden the moment the snippet is usable. Ours opens theirs
 * (`show` + `open`); when the person closes the conversation Zendesk's launcher
 * would come back, so `close` hides it again. Unread replies still reach the
 * person: Messaging reports the count and the launcher wears it as a badge.
 *
 * Module-level state, because there is one widget per page and the launcher
 * can mount before or after the snippet loads.
 */
const launcherReady = ref(false);
const launcherOpen = ref(false);
const launcherUnread = ref(0);

/** Back to "no widget yet" — for tests and for a full session teardown. */
export const resetAtlasLauncher = (): void => {
  launcherReady.value = false;
  launcherOpen.value = false;
  launcherUnread.value = 0;
};

export const useAtlasLauncher = () => ({
  ready: readonly(launcherReady),
  isOpen: readonly(launcherOpen),
  unread: readonly(launcherUnread),
  /** The launcher's click: bring Zendesk's window up. */
  openConversation: (): void => {
    launcherOpen.value = true;
    show();
    open();
  },
});

/** Wire the custom launcher once the snippet can hear us. */
const takeOverLauncher = (): void => {
  hide();
  launcherReady.value = true;

  // `open` is observed by startZendeskWidget's own handler (it re-sends the
  // sender), which also flips launcherOpen — one listener per event.
  zE('messenger:on', 'close', () => {
    launcherOpen.value = false;
    // Zendesk re-shows its own launcher after close; ours is the only one.
    hide();
  });
  zE('messenger:on', 'unreadMessages', (count: unknown) => {
    launcherUnread.value = Math.max(0, Number(count) || 0);
  });
};

/**
 * Start the widget for this session.
 *
 * Returns false only when there is no key configured — every signed-in session
 * gets the widget, impersonated ones included (see `requester` for who the
 * conversation is then attributed to).
 */
export const startZendeskWidget = (
  user?: User,
  role?: Role,
  impersonatedBy?: Actor | null
): boolean => {
  if (!isConfigured()) return false;

  loadScript(widgetKey(), () => {
    takeOverLauncher();
    describeSender(user, role, impersonatedBy);

    // The two moments immediately before a ticket can be created. Setting the
    // metadata here as well as at load is what makes it actually reach the
    // ticket — see describeSender for why once is not enough.
    zE('messenger:on', 'open', () => {
      launcherOpen.value = true;
      describeSender(user, role, impersonatedBy);
    });
    zE('messenger:on', 'newConversationButtonClicked', () =>
      describeSender(user, role, impersonatedBy)
    );

    // AB#21595 — match the portal's theme now, and follow it on every toggle.
    // The subscription lives for the session (the widget does too); it is never
    // torn down, which is why it is registered once here rather than per open.
    applyZendeskTheme(currentIsDark());
    onThemeChange(applyZendeskTheme);

    // Identity is attempted second and never gates the metadata: if signing is
    // unavailable the ticket must still arrive saying who it is from.
    if (!impersonatedBy) login();
  });

  return true;
};

/**
 * End the Zendesk session when the portal session ends (AB#21166).
 *
 * Zendesk keeps its own authenticated session in its own storage, which our
 * logout does not touch. Without this, two things go wrong and neither is
 * theoretical on a shared machine:
 *
 *   - the next person to sign in on that browser opens the widget and reads the
 *     previous person's conversation history, now under a real name and email;
 *   - `loginUser` with a DIFFERENT user while a session is still live is
 *     undefined behaviour in Zendesk, so the second person's tickets can land on
 *     the first person's identity.
 *
 * Resolves rather than rejects on failure, and never waits long: a support
 * widget must not be able to trap somebody in a session they asked to leave. If
 * Zendesk is slow or blocked we log the person out of the portal anyway — the
 * alternative is worse than an uncleared widget.
 */
export const endZendeskSession = (timeoutMs = 1500): Promise<void> =>
  new Promise((resolve) => {
    if (!window.zE) {
      resolve();
      return;
    }

    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    setTimeout(done, timeoutMs);
    zE('messenger', 'logoutUser', done);
  });
